import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
    ShieldCheck, Loader2, Building2, Clock, CheckCircle2, XCircle, AlertTriangle,
    FileText, PlayCircle, PauseCircle, RefreshCw,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

const STATUS_META = {
    consent_pending: { label: "Awaiting Your Authorization", icon: Clock, cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
    ready_for_processing: { label: "Authorized · Queued", icon: PlayCircle, cls: "bg-sky-500/15 text-sky-700 dark:text-sky-400" },
    in_progress: { label: "In Progress", icon: RefreshCw, cls: "bg-violet-500/15 text-violet-700 dark:text-violet-400" },
    completed: { label: "Completed", icon: CheckCircle2, cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
    review_required: { label: "Under Review", icon: AlertTriangle, cls: "bg-orange-500/15 text-orange-700 dark:text-orange-400" },
    cancelled: { label: "Cancelled", icon: PauseCircle, cls: "bg-slate-500/15 text-slate-600 dark:text-slate-400" },
    declined: { label: "Declined by You", icon: XCircle, cls: "bg-rose-500/15 text-rose-700 dark:text-rose-400" },
};
const StatusPill = ({ status }) => {
    const m = STATUS_META[status] || { label: status || "—", icon: Clock, cls: "bg-muted text-muted-foreground" };
    const Icon = m.icon;
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${m.cls}`}>
            <Icon size={10} /> {m.label}
        </span>
    );
};

export default function TalentScreeningRequests() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reviewing, setReviewing] = useState(null); // {req, disclosure}
    const [accept1, setAccept1] = useState(false);
    const [accept2, setAccept2] = useState(false);
    const [signature, setSignature] = useState("");
    const [declining, setDeclining] = useState(null);
    const [declineReason, setDeclineReason] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/talent/screening-requests");
            setRows(data.results || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const openReview = async (req) => {
        setBusy(true);
        try {
            const { data } = await api.get(`/talent/screening-requests/${req.id}/disclosure`);
            setReviewing({ req, disclosure: data });
            setAccept1(false); setAccept2(false); setSignature("");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    const submitAuthorize = async () => {
        if (!accept1 || !accept2 || !signature.trim()) return;
        setBusy(true);
        try {
            await api.post(`/talent/screening-requests/${reviewing.req.id}/authorize`, {
                signature_name: signature.trim(),
                accept_disclosure: accept1,
                accept_authorization: accept2,
            });
            toast.success("Authorization recorded — Simonara ops will run the check.");
            setReviewing(null);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    const submitDecline = async () => {
        setBusy(true);
        try {
            await api.post(`/talent/screening-requests/${declining.id}/decline`,
                            { reason: declineReason.trim() || null });
            toast.info("You declined this screening.");
            setDeclining(null); setDeclineReason("");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid="talent-screenings-page" className="space-y-6">
            <div>
                <p className="overline">Employer Screenings</p>
                <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">Background Screening Requests</h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    When an employer decides to move forward with you, they may request a background screening.
                    Review the disclosure and authorize (or decline) here. These are separate from your
                    Simonara Verified™ Passport verifications and are private between you and that employer only.
                </p>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : rows.length === 0 ? (
                <div data-testid="talent-screenings-empty" className="rounded-2xl border border-dashed border-border bg-card/60 p-12 text-center">
                    <ShieldCheck className="mx-auto h-8 w-8 text-muted-foreground" />
                    <h3 className="mt-3 text-base font-semibold text-foreground">No screening requests yet.</h3>
                    <p className="text-sm text-muted-foreground mt-1">Employers will request screenings from here when they move forward with you.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {rows.map((r) => (
                        <motion.div
                            key={r.id}
                            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                            data-testid={`talent-screening-row-${r.id}`}
                            className="rounded-2xl border border-border bg-card p-5"
                        >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="font-heading font-semibold text-foreground">{r.package_label}</h3>
                                        <StatusPill status={r.status} />
                                    </div>
                                    <div className="mt-1.5 text-xs text-muted-foreground grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                                        <div className="inline-flex items-center gap-1"><Building2 size={11} /> {r.employer_company || "An employer"}</div>
                                        <div className="inline-flex items-center gap-1"><Clock size={11} /> requested {new Date(r.requested_at).toLocaleString()}</div>
                                    {r.job_id && (
                                        <div className="inline-flex items-center gap-1">
                                            <FileText size={11} />
                                            {r.job_title ? <span>role: <span className="font-semibold text-foreground">{r.job_title}</span></span> : "for one of their open roles"}
                                        </div>
                                    )}
                                        {r.consent_at && <div className="inline-flex items-center gap-1"><CheckCircle2 size={11} className="text-emerald-600" /> authorized {new Date(r.consent_at).toLocaleString()}</div>}
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2 shrink-0">
                                    {r.status === "consent_pending" && (
                                        <>
                                            <button onClick={() => openReview(r)} disabled={busy} data-testid={`talent-screening-review-${r.id}`} className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 inline-flex items-center gap-1 disabled:opacity-60"><ShieldCheck size={11} /> Review & Authorize</button>
                                            <button onClick={() => setDeclining(r)} disabled={busy} data-testid={`talent-screening-decline-${r.id}`} className="px-3 py-1.5 rounded-full border border-rose-300 text-rose-700 text-xs font-semibold hover:bg-rose-50 dark:hover:bg-rose-500/10 inline-flex items-center gap-1"><XCircle size={11} /> Decline</button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}

            {/* Review & Authorize dialog */}
            <Dialog open={reviewing !== null} onOpenChange={(o) => { if (!o) setReviewing(null); }}>
                <DialogContent data-testid="talent-authorize-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Authorize background screening</DialogTitle>
                        <DialogDescription>
                            From <strong>{reviewing?.disclosure?.employer_company || "the employer"}</strong> — {reviewing?.disclosure?.package_label}.
                        </DialogDescription>
                    </DialogHeader>
                    {reviewing && (
                        <div className="space-y-4 text-sm">
                            <div className="rounded-lg border border-amber-500/40 bg-amber-500/[0.08] px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                                <strong>Legal notice:</strong> {reviewing.disclosure.legal_note}
                            </div>
                            <div>
                                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Disclosure ({reviewing.disclosure.disclosure_version})</div>
                                <p className="text-sm text-foreground whitespace-pre-wrap">{reviewing.disclosure.disclosure_text}</p>
                            </div>
                            <div>
                                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Authorization ({reviewing.disclosure.authorization_version})</div>
                                <p className="text-sm text-foreground whitespace-pre-wrap">{reviewing.disclosure.authorization_text}</p>
                            </div>
                            <div className="space-y-2">
                                <label className="flex items-start gap-2">
                                    <Checkbox checked={accept1} onCheckedChange={() => setAccept1(!accept1)} data-testid="talent-authorize-accept-disclosure" className="!rounded-[4px] mt-0.5" />
                                    <span className="text-sm">I have read and understood the disclosure above.</span>
                                </label>
                                <label className="flex items-start gap-2">
                                    <Checkbox checked={accept2} onCheckedChange={() => setAccept2(!accept2)} data-testid="talent-authorize-accept-authorization" className="!rounded-[4px] mt-0.5" />
                                    <span className="text-sm">I authorize this specific employer to obtain a consumer report about me for employment purposes.</span>
                                </label>
                                <div>
                                    <label className="text-xs font-semibold text-foreground">Type your full name as electronic signature</label>
                                    <Input value={signature} onChange={(e) => setSignature(e.target.value)} data-testid="talent-authorize-signature" placeholder="Your full name" />
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <button type="button" onClick={() => setReviewing(null)} data-testid="talent-authorize-cancel" className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted">Cancel</button>
                        <button type="button" onClick={submitAuthorize} disabled={busy || !accept1 || !accept2 || !signature.trim()} data-testid="talent-authorize-submit" className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                            {busy ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />} Authorize
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Decline dialog */}
            <Dialog open={declining !== null} onOpenChange={(o) => { if (!o) { setDeclining(null); setDeclineReason(""); } }}>
                <DialogContent data-testid="talent-decline-dialog">
                    <DialogHeader>
                        <DialogTitle>Decline this screening?</DialogTitle>
                        <DialogDescription>
                            You&apos;re within your rights to decline. This is a terminal state — the employer will
                            see the request is declined and may choose not to proceed with your candidacy.
                        </DialogDescription>
                    </DialogHeader>
                    <Textarea value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} data-testid="talent-decline-reason" placeholder="Optional reason (visible to the employer)" rows={3} />
                    <DialogFooter className="gap-2">
                        <button type="button" onClick={() => { setDeclining(null); setDeclineReason(""); }} data-testid="talent-decline-cancel" className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted">Keep it open</button>
                        <button type="button" onClick={submitDecline} disabled={busy} data-testid="talent-decline-submit" className="inline-flex items-center gap-1 rounded-full bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-60">
                            <XCircle size={11} /> Decline
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
